/**
 * CORP//LLM — Hub Screen (full-bleed shell screen)
 * Tabs: SESSIONS | CHANGELOG | ROADMAP | BUGTRACKER (+ ADMIN for admins).
 * Profil + Einstellungen live as header buttons (their views are header-triggered,
 * not tabs). Replaces the old lobby.ts + settings.ts.
 */
import { clearJwt, getJwt, parseJwt, startGitHubLogin, startDiscordLogin, startGoogleLogin } from '../auth';
import { t, setLang, getLang, SUPPORTED_LANGS } from '../i18n';
import { getProfile, getProviders, getPreferences, getBotCorps, getMotd } from '../api';
import type { PlayerProfile, ProvidersResponse, BotCorp, Motd } from '../api';
import { renderSettings } from './settings';
import { renderAdmin } from './admin';
import { formatVersion, formatVersionFull, formatVersionTooltip } from '../utils/version';

declare const __APP_VERSION__: string;

// ── Shared Constants ──────────────────────────────────────────────────────────

interface SessionRow {
  id: string;
  name: string;
  status: string;
  maxPlayers: number;
  seed: number;
  players: string[];
  playerColors?: string[];
  playerNames?: string[];
  playerAvatars?: string[];
  playerJoinedAt?: string[];
  playerSessionCounts?: number[];
  /** Parallel array: true where the session_players row is a bot.
   *  Server v1.6.3+. Older servers omit it; default to all-false. */
  playerIsBots?: boolean[];
  day: number;
  mode: string;
  createdAt: string;
  isMember: boolean;
  winner?: string;
  botCount?: number;
  hasPassword?: boolean;
}

interface BotSlot {
  corpName: string;
  temperament: string;
  difficulty: string;
  strategy: string;
}

const BOT_TEMPERAMENTS = ['aggressive', 'cautious', 'balanced', 'opportunistic'] as const;
const BOT_DIFFICULTIES = ['easy', 'normal', 'hard'] as const;
const BOT_STRATEGIES   = ['aggressor', 'economist', 'diplomat', 'hacker', 'econhacker', 'aggrodiplomat'] as const;

const CORP_NAME_KEY = 'defaultCorpName';
const ACCENT_KEY    = 'accentColor';

const ACCENT_PRESETS = [
  { name: 'Cyan',    color: '#00f5d4' },
  { name: 'Magenta', color: '#f72585' },
  { name: 'Amber',   color: '#fca311' },
  { name: 'Green',   color: '#06d6a0' },
  { name: 'Purple',  color: '#9b5de5' },
  { name: 'Red',     color: '#e63946' },
  { name: 'Blue',    color: '#4361ee' },
  { name: 'Pink',    color: '#ff6b9d' },
  { name: 'Lime',    color: '#a7f542' },
  { name: 'Orange',  color: '#ff8c42' },
];

// ── City Name Generator (mirrors server namegen.go) ─────────────────────────

const CITY_PREFIXES = [
  'Neo', 'Chrome', 'Neon', 'Synth', 'Ghost',
  'Rust', 'Hex', 'Void', 'Shadow', 'Pulse',
  'Arc', 'Flux', 'Haze', 'Onyx', 'Zero',
  'Crimson', 'Iron', 'Obsidian', 'Null', 'Prism',
  'Amber', 'Cobalt', 'Static', 'Phantom', 'Zinc',
];
const CITY_SUFFIXES = [
  'Osaka', 'Harbor', 'Kowloon', 'Manila', 'Shenzhen',
  'Odessa', 'Tangier', 'Mombasa', 'Havana', 'Lagos',
  'Jakarta', 'Marrakech', 'Vladivostok', 'Caracas', 'Bangalore',
  'Chongqing', 'Nairobi', 'Busan', 'Valparaiso', 'Casablanca',
  'Macau', 'Tashkent', 'Bogota', 'Maputo', 'Karachi',
];

function randomCityName(): string {
  return CITY_PREFIXES[Math.floor(Math.random() * CITY_PREFIXES.length)]
    + ' ' + CITY_SUFFIXES[Math.floor(Math.random() * CITY_SUFFIXES.length)];
}

function localTimeStr(utcHour: number): string {
  const d = new Date();
  d.setUTCHours(utcHour, 0, 0, 0);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const SECTORS_PER_PLAYER: Record<string, number> = { quick: 5, standard: 7, epic: 9 };

// ── Helpers ───────────────────────────────────────────────────────────────────

function savedCorpName(): string {
  try { return localStorage.getItem(CORP_NAME_KEY) ?? ''; } catch { return ''; }
}
function saveCorpName(v: string): void {
  try { localStorage.setItem(CORP_NAME_KEY, v); } catch { /* ignore */ }
}
function savedAccent(): string {
  try { return localStorage.getItem(ACCENT_KEY) ?? ACCENT_PRESETS[0].color; } catch { return ACCENT_PRESETS[0].color; }
}
function saveAccent(v: string): void {
  try { localStorage.setItem(ACCENT_KEY, v); } catch { /* ignore */ }
  document.documentElement.style.setProperty('--cyan', v);
  document.documentElement.style.setProperty('--cyan-dim', v + 'aa');
}

export function escHtml(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Whitelist URL sanitiser for interpolation into href/src. Permits http(s),
 *  mailto, tel, root-relative/hash/query paths and safe inline data:image —
 *  everything else (notably `javascript:`, `data:text/html`, protocol-relative)
 *  collapses to '#'. The result is additionally HTML-escaped so it is safe
 *  inside a double-quoted attribute. */
export function sanitizeUrl(u: unknown): string {
  const s = String(u ?? '').trim();
  if (s === '') return '#';
  // root-relative / hash / query — but not protocol-relative (//evil.com)
  if (/^[/#?]/.test(s) && !s.startsWith('//')) return escHtml(s);
  // safe inline images
  if (/^data:image\/(?:png|jpe?g|gif|webp|avif);base64,[a-z0-9+/=]+$/i.test(s)) return escHtml(s);
  // absolute URL with an allow-listed scheme
  if (/^(?:https?|mailto|tel):/i.test(s)) return escHtml(s);
  return '#';
}

// ── Seeded RNG (Wang hash) ────────────────────────────────────────────────────
function seededRand(seed: number, n: number): number {
  let h = ((seed ^ (n * 0x9e3779b9)) >>> 0);
  h = (Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0);
  h = (Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0);
  return ((h ^ (h >>> 16)) >>> 0) / 0xffffffff;
}

// ── Seed canvas — 8x8 city grid fingerprint ──────────────────────────────────
const GRID_PALETTE: { thresh: number; color: string; alpha: number }[] = [
  { thresh: 0.06, color: '#00f5d4', alpha: 0.95 },
  { thresh: 0.14, color: '#00f5d4', alpha: 0.45 },
  { thresh: 0.28, color: '#06d6a0', alpha: 0.30 },
  { thresh: 0.44, color: '#fca311', alpha: 0.30 },
  { thresh: 0.62, color: '#1a2e2b', alpha: 1.00 },
  { thresh: 1.00, color: '#090f0e', alpha: 1.00 },
];

function buildSeedCanvas(seed: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width  = 72;
  canvas.height = 72;
  canvas.className = 'hub-seed-canvas';

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const CELL = 8;
  const GAP  = 1;
  const SIZE = CELL + GAP;

  ctx.fillStyle = '#060c0b';
  ctx.fillRect(0, 0, 72, 72);

  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const r = seededRand(seed, row * 8 + col);
      const entry = GRID_PALETTE.find(p => r < p.thresh)!;
      ctx.globalAlpha = entry.alpha;
      ctx.fillStyle   = entry.color;
      ctx.fillRect(col * SIZE, row * SIZE, CELL, CELL);
    }
  }

  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      if (seededRand(seed, row * 8 + col) < 0.06) {
        ctx.globalAlpha = 0.9;
        ctx.fillStyle   = '#ffffff';
        ctx.fillRect(col * SIZE + 3, row * SIZE + 3, 2, 2);
      }
    }
  }

  ctx.globalAlpha = 1;
  return canvas;
}

// ── Player dot-bar ────────────────────────────────────────────────────────────
function dotBar(cur: number, max: number): string {
  const slots = Math.min(max, 12);
  const filled = Math.round((cur / max) * slots);
  let html = '<span class="hub-dotbar">';
  for (let i = 0; i < slots; i++) {
    html += `<span class="hub-dot${i < filled ? ' on' : ''}"></span>`;
  }
  html += '</span>';
  if (max > 12) html += ` <span class="hub-dot-label">${cur}/${max}</span>`;
  return html;
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════════════════════

export function renderHub(
  serverBase: string,
  onJoin: (sessionId: string, corpName: string) => void,
  onApiMode?: (sessionId: string) => void,
): HTMLElement {
  const el = document.createElement('div');
  el.className = 'screen hub-screen';

  const jwt = getJwt() ?? '';
  const jwtPayload = jwt ? parseJwt(jwt) : null;
  const isUserAdmin = jwtPayload?.admin === true;
  const isBetaUser = jwtPayload?.beta === true || isUserAdmin || !import.meta.env.PROD;
  let adminMode = isUserAdmin && (localStorage.getItem('corpllm_admin_mode') === '1');

  const currentAccent = savedAccent();
  document.documentElement.style.setProperty('--cyan', currentAccent);
  document.documentElement.style.setProperty('--cyan-dim', currentAccent + 'aa');

  // ── Injected CSS ──────────────────────────────────────────────────────────

  el.innerHTML = `<style>
    /* ── Hub Layout ── */
    /* Hub is a full-bleed shell screen — kill the global .screen padding (20px)
       so the hub reaches every edge (no atmosphere frame), and keep .hub-root
       transparent so the #main atmosphere (hex weave + vignette) shows through. */
    .screen.hub-screen { padding: 0; height: 100%; overflow: hidden; }
    .hub-root { display:flex; flex-direction:column; height:100%; overflow:hidden; font-family:var(--mono); background:transparent; }

    /* ── Header ── */
    .hub-header { display:flex; align-items:center; justify-content:space-between; padding:0 24px; height:54px; background:linear-gradient(180deg, var(--bg-deep), rgba(10,14,23,0.9)); border-bottom:1px solid var(--line); box-shadow:0 8px 24px rgba(0,0,0,0.5); flex-shrink:0; }
    .hub-brand { display:flex; align-items:center; gap:13px; }
    .hub-logo { font-family:var(--brand); font-size:18px; letter-spacing:1px; color:var(--text-hi); text-shadow:0 0 18px rgba(var(--ac),0.25); }
    .hub-logo .hub-acc { color:var(--cyan); }
    .hub-label { font-family:var(--display); font-size:11px; letter-spacing:3px; color:var(--text-lo); text-transform:uppercase; }
    .hub-header-right { display:flex; align-items:center; gap:10px; }
    .hub-avatar-small { width:28px; height:28px; border-radius:50%; border:2px solid var(--cyan); object-fit:cover; cursor:pointer; transition:border-color 0.15s; position:relative; }
    .hub-avatar-small:hover { border-color:#fff; }
    .hub-avatar-wrap { position:relative; display:inline-block; }
    .hub-avatar-popup { display:none; position:absolute; top:calc(100% + 8px); right:0; z-index:1200;
      width:320px; height:320px; border-radius:50%; border:3px solid var(--cyan);
      box-shadow:0 12px 48px rgba(0,0,0,0.7), 0 0 24px rgba(var(--ac),0.15);
      overflow:hidden; background:var(--bg-panel); pointer-events:none;
      animation:popupFadeIn 0.15s ease; }
    .hub-avatar-wrap:hover .hub-avatar-popup { display:block; }
    .hub-avatar-popup img { width:100%; height:100%; object-fit:cover; image-rendering:high-quality; }
    @keyframes popupFadeIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
    /* .hub-hbtn → shared .btn.sm (+ .active); marker kept */

    /* ── Tab bar ── */
    .hub-tabs { display:flex; gap:2px; padding:0 28px; background:var(--bg-deep); border-bottom:1px solid var(--border); flex-shrink:0; }
    /* .hub-tab / .hub-subtab → shared .tab (markers kept for ARIA + JS) */
    .hub-subtabs { display:flex; gap:4px; margin-bottom:14px; border-bottom:1px solid var(--line); }

    /* ── Body ── */
    .hub-body { flex:1; overflow-y:auto; padding:24px 28px; }
    .hub-body::-webkit-scrollbar { width:4px; }
    .hub-body::-webkit-scrollbar-thumb { background:var(--border); border-radius:2px; }
    .hub-view { display:none; }
    .hub-view.active { display:block; }

    /* ═══════════════════ SESSIONS TAB ═══════════════════ */

    /* ── Actions bar ── */
    .hub-actions { display:flex; align-items:center; gap:10px; margin-bottom:20px; }
    /* .hub-btn → shared .btn.cyan · .secondary → .btn · .danger → .btn.red (markers kept) */
    .hub-status { font-size:0.85rem; color:var(--text-dim); margin-left:4px; }
    .hub-err { font-size:0.85rem; color:var(--red); margin-left:4px; }

    /* ── Create form ── */
    .hub-create { border:1px solid rgba(var(--ac),0.25); padding:16px 20px; margin-bottom:20px; display:none; background:rgba(var(--ac),0.02); }
    .hub-create.open { display:block; }
    .hub-create-title { font-size:0.85rem; letter-spacing:0.2em; color:var(--cyan); margin-bottom:14px; }
    .hub-form-row { display:flex; align-items:center; gap:12px; margin-bottom:10px; }
    .hub-form-label { font-size:0.85rem; color:var(--text-mid); width:130px; flex-shrink:0; letter-spacing:0.06em; }
    .hub-form-input { background:none; border:1px solid var(--border); color:var(--cyan); font-family:var(--mono); font-size:0.95rem; padding:6px 12px; flex:1; outline:none; }
    .hub-form-input:focus { border-color:var(--cyan); }
    .hub-form-input-sm { width:90px; flex:none; }
    .hub-form-input[type="range"] { -webkit-appearance:none; appearance:none; height:4px; background:var(--border); outline:none; cursor:pointer; }
    .hub-form-input[type="range"]::-webkit-slider-thumb { -webkit-appearance:none; width:16px; height:16px; border-radius:50%; background:var(--cyan); border:2px solid var(--bg); cursor:pointer; }
    .hub-form-input[type="range"]::-moz-range-thumb { width:16px; height:16px; border-radius:50%; background:var(--cyan); border:2px solid var(--bg); cursor:pointer; }
    .hub-form-hint { font-size:0.78rem; color:var(--text-lo); }
    .hub-create-btns { display:flex; gap:10px; margin-top:8px; }

    /* ── Bot slots ── */
    .hub-bot-section { margin-top:6px; }
    .hub-bot-rows { display:flex; flex-direction:column; gap:10px; }
    .hub-bot-card { display:flex; flex-direction:column; gap:3px; }
    .hub-bot-row { display:flex; align-items:center; gap:6px; }
    .hub-bot-row select { background:var(--bg); border:1px solid var(--border); color:var(--text-mid); font-family:var(--mono); font-size:0.82rem; padding:4px 6px; outline:none; }
    .hub-bot-row select:focus { border-color:var(--cyan); }
    .hub-bot-idx { font-size:0.75rem; color:var(--text-dim); width:18px; text-align:right; flex-shrink:0; }
    .hub-bot-tagline { font-size:0.78rem; color:var(--text-dim); font-style:italic; padding-left:26px; line-height:1.3; max-width:420px; }
    /* .hub-bot-remove → shared .btn.sm.red (marker kept; flex-shrink inline) */
    .hub-bot-actions { display:flex; gap:6px; margin-top:6px; }
    .hub-bot-hint { font-size:0.75rem; color:var(--text-lo); margin-top:4px; }

    /* ── Section divider ── */
    .hub-section-head { display:flex; align-items:center; gap:12px; margin-bottom:14px; }
    .hub-section-title { font-size:0.78rem; letter-spacing:0.22em; color:var(--text-lo); text-transform:uppercase; white-space:nowrap; }
    .hub-section-line { flex:1; height:1px; background:var(--border); }

    /* ── Session cards ── */
    .hub-cards { display:flex; flex-direction:column; gap:10px; }
    .hub-card { border:1px solid var(--line); background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); padding:14px 18px;
      display:grid; grid-template-columns:72px 1fr auto; gap:16px; align-items:center; }
    .hub-card.is-mine { border-color:rgba(252,163,17,0.35); }
    .hub-card.is-running { opacity:0.7; }
    .hub-card.is-finished { opacity:0.5; }
    .hub-card-thumb { width:72px; height:72px; flex-shrink:0; border:1px solid rgba(255,255,255,0.05); overflow:hidden; }
    .hub-seed-canvas { display:block; }
    .hub-card-name { font-size:1.1rem; letter-spacing:0.12em; color:var(--text-hi); margin-bottom:6px; display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
    .hub-card-seed { font-size:0.78rem; color:var(--text-lo); }
    .hub-card-meta { display:flex; align-items:center; gap:12px; margin-bottom:8px; font-size:0.85rem; color:var(--text-mid); flex-wrap:wrap; }
    .hub-card-players-list { display:flex; flex-wrap:wrap; gap:4px; }
    .hub-card-player-chip { font-size:0.78rem; padding:2px 8px; background:rgba(var(--ac),0.05); border:1px solid rgba(var(--ac),0.12); color:var(--text-mid); cursor:default; position:relative; }
    .hub-card-player-chip.me { border-color:rgba(252,163,17,0.4); color:var(--amber); background:rgba(252,163,17,0.06); }
    .hub-card-player-chip.bot { border-color:rgba(155,93,229,0.35); color:#c9a9f5; background:rgba(155,93,229,0.05); }
    .hub-player-popover { display:none; position:absolute; bottom:calc(100% + 6px); left:50%; transform:translateX(-50%); z-index:1200;
      background:rgba(10,12,20,0.97); border:1px solid rgba(var(--ac),0.35); border-radius:8px;
      padding:12px 16px; min-width:220px; pointer-events:none;
      box-shadow:0 8px 32px rgba(0,0,0,0.6), 0 0 12px rgba(var(--ac),0.08);
      font-family:var(--mono); animation:popupFadeIn 0.12s ease; white-space:nowrap; }
    .hub-card-player-chip:hover .hub-player-popover,
    .hub-card-player-chip:focus-within .hub-player-popover { display:block; }
    .hub-card-player-chip:focus-visible { outline:1px solid var(--cyan); outline-offset:1px; }
    .hub-player-popover::after { content:''; position:absolute; top:100%; left:50%; transform:translateX(-50%);
      border:6px solid transparent; border-top-color:rgba(var(--ac),0.35); }
    .hub-popover-header { display:flex; align-items:center; gap:12px; }
    .hub-popover-color { width:10px; height:10px; border-radius:50%; border:1px solid rgba(255,255,255,0.15); flex-shrink:0; }
    .hub-popover-name { font-size:0.88rem; color:var(--text-hi); letter-spacing:0.1em; }
    .hub-popover-subname { font-size:0.72rem; color:var(--text-lo); margin-top:2px; }
    .hub-popover-badge { display:inline-flex; align-items:center; gap:6px; margin-top:8px; padding-top:7px; border-top:1px solid rgba(255,255,255,0.06); }
    .hub-popover-label { font-size:0.7rem; color:var(--text-dim); text-transform:uppercase; letter-spacing:0.12em; }
    .hub-popover-portrait { width:56px; height:56px; border-radius:50%; border:2px solid rgba(var(--ac),0.25); object-fit:cover; flex-shrink:0; }
    .hub-popover-stats { display:grid; grid-template-columns:auto auto; gap:3px 12px; margin-top:8px; padding-top:7px; border-top:1px solid rgba(255,255,255,0.06); }
    .hub-popover-stat-label { font-size:0.68rem; color:var(--text-dim); text-transform:uppercase; letter-spacing:0.1em; }
    .hub-popover-stat-value { font-size:0.75rem; color:var(--cyan); text-align:right; letter-spacing:0.05em; }
    .hub-card-right { display:flex; flex-direction:column; align-items:flex-end; gap:6px; }

    /* ── Status badge ── */
    .hub-badge { display:inline-flex; align-items:center; gap:5px; font-size:0.78rem; letter-spacing:0.12em; padding:3px 10px; border:1px solid; white-space:nowrap; }
    .hub-badge::before { content:''; width:6px; height:6px; border-radius:50%; flex-shrink:0; }
    .hub-badge.running { color:var(--green); border-color:rgba(46,204,113,0.4); background:rgba(46,204,113,0.06); }
    .hub-badge.running::before { background:var(--green); box-shadow:0 0 6px var(--green); }
    .hub-badge.lobby { color:var(--cyan); border-color:rgba(var(--ac),0.4); background:rgba(var(--ac),0.04); }
    .hub-badge.lobby::before { background:var(--cyan); box-shadow:0 0 6px var(--cyan); }
    .hub-badge.full { color:var(--red); border-color:rgba(231,76,60,0.4); background:rgba(231,76,60,0.04); }
    .hub-badge.full::before { background:var(--red); }
    .hub-badge.here { color:var(--amber); border-color:rgba(252,163,17,0.4); background:rgba(252,163,17,0.04); }
    .hub-badge.here::before { background:var(--amber); box-shadow:0 0 6px var(--amber); }
    .hub-badge.finished { color:var(--text-lo); border-color:rgba(255,255,255,0.2); background:rgba(255,255,255,0.03); }
    .hub-badge.finished::before { background:var(--text-lo); }
    .hub-winner { font-size:0.82rem; color:var(--amber); }
    .hub-section-gap { margin-top:24px; }

    /* ── Dot-bar ── */
    .hub-dotbar { display:inline-flex; gap:3px; align-items:center; }
    .hub-dot { width:5px; height:5px; border-radius:50%; border:1px solid rgba(var(--ac),0.3); flex-shrink:0; }
    .hub-dot.on { background:var(--cyan); border-color:var(--cyan); }
    .hub-dot-label { font-size:0.82rem; color:var(--text-mid); }

    /* ── Mode badge ── */
    .hub-mode { font-size:0.75rem; letter-spacing:0.12em; padding:2px 8px; border:1px solid rgba(155,93,229,0.35); color:var(--violet); background:rgba(155,93,229,0.06); white-space:nowrap; cursor:help; }
    .hub-mode.quick { border-color:rgba(46,204,113,0.35); color:var(--green); background:rgba(46,204,113,0.06); }
    .hub-mode.epic { border-color:rgba(231,76,60,0.35); color:var(--red); background:rgba(231,76,60,0.06); }

    /* ── Join action ── */
    /* .hub-join → shared .btn.amber · .hub-api-btn → shared .btn.sm (markers kept) */
    .hub-join-as { font-size:0.82rem; color:var(--cyan); font-family:var(--mono); letter-spacing:0.08em; opacity:0.75; text-transform:uppercase; white-space:nowrap; }
    .hub-join-input { width:160px; padding:5px 10px; font-size:0.88rem; flex:none; }
    .hub-unavail { font-size:0.85rem; color:var(--text-lo); }

    /* ── Leave / delete ── */
    /* .hub-leave / .hub-delete → shared .btn.sm.red (markers kept) */
    .hub-admin-badge { font-size:0.72rem; letter-spacing:0.12em; padding:2px 8px; border:1px solid rgba(231,76,60,0.4); color:var(--red); background:rgba(231,76,60,0.06); }

    /* ── Session limit indicator ── */
    .hub-limit { font-size:0.82rem; letter-spacing:0.1em; color:var(--text-mid); }
    .hub-limit.at-cap { color:var(--red); }
    .hub-limit-hint { font-size:0.78rem; color:var(--red); margin-left:8px; }

    /* ── Empty / loading ── */
    /* .hub-empty → shared .empty · .hub-loading → shared .empty.loading (markers kept) */
    .hub-version { margin-top:40px; padding:12px 0; text-align:right; font-size:0.68rem; color:var(--text-lo); font-family:var(--mono); letter-spacing:0.12em; opacity:0.5; }
    .hub-meta-panel { background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); border:1px solid var(--line); border-radius:6px; padding:14px 16px; }
    .hub-meta-item { display:flex; align-items:center; gap:8px; padding:5px 0; font-size:12px; font-family:var(--mono); color:var(--text-mid); border-bottom:1px solid var(--line); }
    .hub-meta-item:last-child { border-bottom:none; }
    .hub-meta-item a { color:var(--cyan); text-decoration:none; }
    .hub-meta-item a:hover { text-decoration:underline; }
    .hub-meta-status { font-size:10px; padding:1px 5px; border-radius:3px; font-family:var(--mono); white-space:nowrap; }
    .hub-meta-status.done { background:rgba(6,214,160,0.12); color:var(--green); }
    .hub-meta-status.todo { background:rgba(100,120,140,0.12); color:var(--text-lo); }
    .hub-meta-status.ready { background:rgba(252,163,17,0.12); color:var(--amber); }
    .hub-meta-status.progress { background:rgba(var(--ac),0.12); color:var(--cyan); }
    .hub-meta-label { font-size:9px; padding:1px 4px; border-radius:2px; background:rgba(100,120,140,0.1); color:var(--text-lo); }
    .hub-meta-type { font-size:10px; padding:1px 5px; border-radius:3px; font-family:var(--mono); white-space:nowrap; text-transform:uppercase; }
    .hub-meta-type.feat { background:rgba(var(--ac),0.12); color:var(--cyan); }
    .hub-meta-type.fix { background:rgba(252,163,17,0.12); color:var(--amber, #fca311); }
    .hub-meta-type.refactor { background:rgba(100,120,200,0.12); color:var(--text-lo); }
    .hub-meta-type.chore { background:rgba(100,120,140,0.08); color:var(--text-dim); }
    .hub-meta-version-head { font-size:11px; color:var(--text-hi); font-weight:600; padding:8px 0 4px; border-bottom:1px solid var(--line); margin-bottom:4px; }
    .hub-meta-version-head:first-child { padding-top:0; }

    /* ═══════════════════ PROFIL TAB ═══════════════════ */

    .hub-profile { max-width:600px; }
    .hub-profile-header { display:flex; align-items:center; gap:24px; margin-bottom:32px; }
    .hub-avatar-large { width:80px; height:80px; border-radius:50%; border:3px solid var(--cyan); object-fit:cover; box-shadow:0 0 16px rgba(var(--ac),0.25); cursor:pointer; }
    .hub-profile-header .hub-avatar-wrap .hub-avatar-popup { top:0; right:auto; left:calc(100% + 16px); transform:none; width:240px; height:240px; animation:popupFadeInRight 0.15s ease; }
    @keyframes popupFadeInRight { from { opacity:0; transform:translateX(4px); } to { opacity:1; transform:translateX(0); } }
    .hub-profile-name { font-size:1.3rem; letter-spacing:0.12em; color:var(--text-hi); margin-bottom:4px; }
    .hub-profile-email { font-size:0.82rem; color:var(--text-lo); font-family:var(--mono); }
    .hub-profile-section { margin-bottom:28px; }
    .hub-profile-section-title { font-size:11px; color:var(--text-dim); text-transform:uppercase; letter-spacing:0.15em; margin-bottom:10px; padding-bottom:4px; border-bottom:1px solid rgba(255,255,255,0.06); font-family:var(--mono); }
    .hub-provider-row { display:flex; align-items:center; gap:12px; padding:8px 0; border-bottom:1px solid rgba(255,255,255,0.03); }
    .hub-provider-icon { width:22px; height:22px; display:flex; align-items:center; justify-content:center; font-size:14px; }
    .hub-provider-name { flex:1; font-size:0.85rem; color:var(--text-mid); }
    .hub-provider-status { font-size:0.78rem; font-family:var(--mono); }
    .hub-provider-linked { color:var(--green); }
    .hub-provider-unlinked { color:var(--text-lo); }
    /* .hub-link-btn → shared .btn.sm.cyan (marker kept) */
    .hub-stat-row { display:flex; align-items:center; gap:12px; padding:6px 0; font-size:0.85rem; }
    .hub-stat-label { color:var(--text-mid); width:160px; flex-shrink:0; font-size:0.78rem; }
    .hub-stat-value { color:var(--cyan); font-family:var(--mono); font-size:0.85rem; }
    /* .hub-logout → shared .btn.red (marker kept; margin-top inline) */

    /* ═══════════════════ SETTINGS TAB ═══════════════════ */

    .hub-settings { max-width:1200px; }

    /* ── Responsive (narrow / mobile) — hub was desktop-only before ── */
    @media (max-width: 720px) {
      .hub-header { padding:0 12px; height:auto; min-height:48px; flex-wrap:wrap; row-gap:6px; }
      .hub-header-right { flex-wrap:wrap; gap:4px; }
      .hub-tabs { overflow-x:auto; -webkit-overflow-scrolling:touch; }
      .hub-tab { white-space:nowrap; }
      .hub-body { padding:14px 12px; }
      .hub-form-row { flex-wrap:wrap; align-items:flex-start; gap:4px; }
      .hub-form-label { width:100%; }
    }
  </style>

  <div class="hub-root">

    <!-- ── Header ── -->
    <div class="hub-header">
      <div class="hub-brand"><div class="hub-logo">CORP<span class="hub-acc">//</span>LLM</div><span class="hub-label">${t('hub.title') !== 'hub.title' ? t('hub.title') : 'HUB'}</span></div>
      <div class="hub-header-right">
        <a class="hub-hbtn btn sm" href="https://wiki.corpllm.io" target="_blank" rel="noopener" title="${t('hub.help')}">${t('hub.help')}</a>
        <button class="hub-hbtn btn sm" id="hub-bug-btn" title="${t('hub.report_bug')}">${t('hub.report_bug')}</button>
        ${isUserAdmin ? `<button class="hub-hbtn btn sm${adminMode ? ' active' : ''}" id="hub-admin-btn" title="${t('settings.admin_mode')}">ADMIN</button>` : ''}
        <button class="hub-hbtn btn sm" id="hub-profil-btn">${t('hub.tab_profil')}</button>
        <button class="hub-hbtn btn sm" id="hub-settings-btn">${t('hub.tab_settings')}</button>
        <button class="hub-hbtn btn sm" id="hub-lang-btn">${t('lang.toggle')}</button>
        <div class="hub-avatar-wrap" id="hub-avatar-wrap" style="display:none">
          <img class="hub-avatar-small" id="hub-header-avatar" src="" alt="${t('hub.tab_profil')}" />
          <div class="hub-avatar-popup"><img id="hub-avatar-popup-img" src="" alt="" /></div>
        </div>
      </div>
    </div>

    <!-- ── Tab bar ── -->
    <div class="hub-tabs">
      <button class="hub-tab tab active" data-tab="sessions">${t('hub.tab_sessions')}</button>
      <button class="hub-tab tab" data-tab="changelog">Changelog</button>
      <button class="hub-tab tab" data-tab="roadmap">Roadmap</button>
      <button class="hub-tab tab" data-tab="bugs">Bugtracker</button>
      ${isUserAdmin ? `<button class="hub-tab tab" data-tab="admin">ADMIN</button>` : ''}
    </div>

    <!-- ── Body ── -->
    <div class="hub-body">

      <!-- ═══ SESSIONS VIEW ═══ -->
      <div class="hub-view active" id="hub-view-sessions">

        <div id="hub-motd-slot"></div>

        <div class="hub-actions">
          <button class="hub-btn btn cyan" id="hub-new-btn">${t('lobby.new_city_btn')}</button>
          <span class="hub-limit" id="hub-limit"></span>
          <span class="hub-limit-hint" id="hub-limit-hint" style="display:none"></span>
          <button class="hub-btn secondary btn" id="hub-refresh-btn" title="${t('lobby.refresh_btn')}">${t('lobby.refresh_btn')}</button>
          <span class="hub-status" id="hub-status"></span>
          <span class="hub-err" id="hub-err"></span>
        </div>

        <div id="hub-beta-banner" style="display:none;margin-bottom:12px;padding:10px 14px;background:rgba(252,163,17,0.08);border:1px solid rgba(252,163,17,0.3);border-radius:4px;font-family:var(--mono);font-size:12px;color:#fca311">
          ${t('lobby.beta_banner')}
        </div>

        <div class="hub-create" id="hub-create-form">
          <div class="hub-create-title">${t('lobby.create_title')}</div>
          <div class="hub-form-row">
            <span class="hub-form-label">${t('lobby.corp_name_label')}</span>
            <input class="hub-form-input" id="hub-create-corp" type="text" maxlength="40"
              value="${escHtml(savedCorpName())}" placeholder="${t('lobby.corp_placeholder')}" autocomplete="off" />
          </div>
          <div class="hub-form-row">
            <span class="hub-form-label">${t('lobby.city_name_label')}</span>
            <input class="hub-form-input" id="hub-city-name" type="text" maxlength="40"
              placeholder="${t('lobby.city_placeholder')}" autocomplete="off" />
          </div>
          <div class="hub-form-row">
            <span class="hub-form-label">${t('lobby.seed_label')}</span>
            <input class="hub-form-input hub-form-input-sm" id="hub-seed" type="number" min="0" max="999999" value="0" autocomplete="off" />
            <span class="hub-form-hint">${t('lobby.seed_hint')}</span>
          </div>
          <div class="hub-form-row">
            <span class="hub-form-label">${t('lobby.max_players_label')}</span>
            <input class="hub-form-input hub-form-input-sm" id="hub-max-players" type="range" min="1" max="8" value="8" style="width:120px;flex:none" />
            <span class="hub-form-hint" id="hub-max-players-val">8</span>
          </div>
          <div class="hub-form-row">
            <span class="hub-form-label">${t('lobby.mode_label')}</span>
            <select class="hub-form-input hub-form-input-sm" id="hub-mode" style="width:auto;flex:none">
              <option value="quick">${t('lobby.mode_quick')}</option>
              <option value="standard" selected>${t('lobby.mode_standard')}</option>
              <option value="epic">${t('lobby.mode_epic')}</option>
            </select>
          </div>
          <div class="hub-form-row">
            <span class="hub-form-label">${t('lobby.city_size_label')}</span>
            <span class="hub-form-hint" id="hub-city-size">40 ${t('lobby.city_size_sectors')}</span>
          </div>
          <div class="hub-form-row">
            <span class="hub-form-label">${t('lobby.deadline_label')}</span>
            <select class="hub-form-input hub-form-input-sm" id="hub-deadline" style="width:auto;flex:none">
              <option value="18">18:00 UTC (${localTimeStr(18)})</option>
              <option value="20">20:00 UTC (${localTimeStr(20)})</option>
              <option value="22" selected>22:00 UTC (${localTimeStr(22)})</option>
              <option value="0">00:00 UTC (${localTimeStr(0)})</option>
            </select>
          </div>
          <div class="hub-form-row">
            <span class="hub-form-label">${t('lobby.password_label')}</span>
            <input class="hub-form-input hub-form-input-sm" id="hub-create-password" type="password" maxlength="64"
              placeholder="${t('lobby.password_placeholder')}" autocomplete="new-password" style="width:auto;flex:none" />
            <span class="hub-form-hint">${t('lobby.password_hint')}</span>
          </div>
          <div class="hub-form-row" style="align-items:flex-start">
            <span class="hub-form-label" style="padding-top:6px">${t('lobby.bot_section')}</span>
            <div class="hub-bot-section" style="flex:1">
              <div class="hub-bot-rows" id="hub-bot-rows"></div>
              <div class="hub-bot-actions">
                <button class="hub-btn secondary btn" id="hub-bot-add" type="button">${t('lobby.bot_add')}</button>
                <button class="hub-btn secondary btn" id="hub-bot-fill" type="button">${t('lobby.bot_fill')}</button>
                <button class="hub-btn secondary btn" id="hub-bot-clear" type="button" style="display:none">${t('lobby.bot_remove_all')}</button>
              </div>
              <div class="hub-bot-hint" id="hub-bot-hint"></div>
            </div>
          </div>
          <div class="hub-create-btns">
            <button class="hub-btn btn cyan" id="hub-create-btn">${t('lobby.create_btn')}</button>
            <button class="hub-btn danger btn red" id="hub-cancel-btn">${t('lobby.cancel_btn')}</button>
          </div>
        </div>

        <div class="hub-loading empty loading" id="hub-loading">${t('lobby.loading')}<span id="hub-dots"></span></div>

        <div id="hub-sec-mine" style="display:none">
          <div class="hub-section-head">
            <span class="hub-section-title">${t('lobby.section_mine')}</span>
            <div class="hub-section-line"></div>
          </div>
          <div class="hub-cards" id="hub-cards-mine"></div>
        </div>

        <div id="hub-sec-new" class="hub-section-gap" style="display:none">
          <div class="hub-section-head">
            <span class="hub-section-title">${t('lobby.section_new')}</span>
            <div class="hub-section-line"></div>
          </div>
          <div class="hub-cards" id="hub-cards-new"></div>
        </div>

        <div id="hub-sec-running" class="hub-section-gap" style="display:none">
          <div class="hub-section-head">
            <span class="hub-section-title">${t('lobby.section_running')}</span>
            <div class="hub-section-line"></div>
          </div>
          <div class="hub-cards" id="hub-cards-running"></div>
        </div>

        <div id="hub-sec-past" class="hub-section-gap" style="display:none">
          <div class="hub-section-head">
            <span class="hub-section-title">${t('lobby.section_past')}</span>
            <div class="hub-section-line"></div>
          </div>
          <div class="hub-cards" id="hub-cards-past"></div>
        </div>

        <div class="hub-empty empty" id="hub-empty-all" style="display:none"></div>

        <div class="hub-version" id="hub-version">${formatVersion()}</div>
      </div>

      <!-- ═══ CHANGELOG VIEW ═══ -->
      <div class="hub-view" id="hub-view-changelog">
        <div class="hub-meta-panel">
          <div class="hub-subtabs">
            <button class="hub-subtab tab active" data-clog="client">Client</button>
            <button class="hub-subtab tab" data-clog="server">Server</button>
          </div>
          <div class="hub-meta-items" id="hub-changelog-client"></div>
          <div class="hub-meta-items" id="hub-changelog-server" style="display:none"></div>
        </div>
      </div>

      <!-- ═══ ROADMAP VIEW ═══ -->
      <div class="hub-view" id="hub-view-roadmap">
        <div class="hub-meta-panel"><div class="hub-meta-items" id="hub-roadmap-items"></div></div>
      </div>

      <!-- ═══ BUGTRACKER VIEW ═══ -->
      <div class="hub-view" id="hub-view-bugs">
        <div class="hub-meta-panel">
          <button class="hub-btn btn cyan" id="hub-bug-open-btn" style="margin-bottom:14px">${t('hub.report_bug')}</button>
          <div class="hub-meta-items" id="hub-bugs-items"></div>
        </div>
      </div>

      <!-- ═══ PROFIL VIEW ═══ -->
      <div class="hub-view" id="hub-view-profil">
        <div class="hub-profile" id="hub-profile-content">
          <div class="hub-loading empty loading" id="hub-profile-loading">${t('hub.loading_profile')}</div>
        </div>
      </div>

      <!-- ═══ SETTINGS VIEW ═══ -->
      <div class="hub-view" id="hub-view-settings">
        <div class="hub-settings" id="hub-settings-content"></div>
      </div>

      ${isUserAdmin ? `<!-- ═══ ADMIN VIEW ═══ -->
      <div class="hub-view" id="hub-view-admin">
        <div id="hub-admin-content"></div>
      </div>` : ''}

    </div>
  </div>`;

  // ══════════════════════════════════════════════════════════════════════════
  // TAB SWITCHING
  // ══════════════════════════════════════════════════════════════════════════

  const tabs    = el.querySelectorAll('.hub-tab') as NodeListOf<HTMLElement>;
  const views   = el.querySelectorAll('.hub-view') as NodeListOf<HTMLElement>;
  let profileLoaded = false;
  let settingsBuilt = false;
  let adminBuilt = false;

  // ── ARIA tablist semantics + roving tabindex + arrow-key navigation ──
  const tablistEl = el.querySelector('.hub-tabs');
  tablistEl?.setAttribute('role', 'tablist');
  tablistEl?.setAttribute('aria-label', 'Hub');
  tabs.forEach(tab => {
    const id = tab.dataset.tab!;
    const active = tab.classList.contains('active');
    tab.setAttribute('role', 'tab');
    tab.id = `hub-tab-${id}`;
    tab.setAttribute('aria-controls', `hub-view-${id}`);
    tab.setAttribute('aria-selected', active ? 'true' : 'false');
    tab.tabIndex = active ? 0 : -1;
    const panel = el.querySelector(`#hub-view-${id}`);
    if (panel) { panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', `hub-tab-${id}`); panel.setAttribute('tabindex', '0'); }
  });
  tablistEl?.addEventListener('keydown', (ev) => {
    const ke = ev as KeyboardEvent;
    const arr = Array.from(tabs);
    const cur = arr.findIndex(tb => tb.getAttribute('aria-selected') === 'true');
    let n = -1;
    if (ke.key === 'ArrowRight' || ke.key === 'ArrowDown') n = (cur + 1) % arr.length;
    else if (ke.key === 'ArrowLeft' || ke.key === 'ArrowUp') n = (cur - 1 + arr.length) % arr.length;
    else if (ke.key === 'Home') n = 0;
    else if (ke.key === 'End') n = arr.length - 1;
    if (n >= 0) { ke.preventDefault(); arr[n].focus(); arr[n].click(); }
  });

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const target = tab.dataset.tab!;
      tabs.forEach(t => { const on = t === tab; t.classList.toggle('active', on); t.setAttribute('aria-selected', on ? 'true' : 'false'); t.tabIndex = on ? 0 : -1; });
      views.forEach(v => v.classList.remove('active'));
      const view = el.querySelector(`#hub-view-${target}`) as HTMLElement;
      if (view) view.classList.add('active');
      // Update URL hash to reflect active tab
      const hash = target === 'sessions' ? '#hub' : `#hub/${target}`;
      history.replaceState(null, '', hash);

      if (target === 'profil' && !profileLoaded) {
        profileLoaded = true;
        loadProfile();
      }
      if (target === 'settings' && !settingsBuilt) {
        settingsBuilt = true;
        buildSettingsTab();
      }
      if (target === 'admin' && !adminBuilt) {
        adminBuilt = true;
        buildAdminTab();
      }
    });
  });

  // Profil + Settings are header buttons now (not tabs): activate their view +
  // clear any active tab. Bug-open button (Bugtracker tab) opens the reporter.
  function activateHubView(id: string): void {
    tabs.forEach(t => { t.classList.remove('active'); t.setAttribute('aria-selected', 'false'); });
    views.forEach(v => v.classList.remove('active'));
    (el.querySelector(`#hub-view-${id}`) as HTMLElement | null)?.classList.add('active');
    history.replaceState(null, '', `#hub/${id}`);
  }
  (el.querySelector('#hub-profil-btn') as HTMLButtonElement | null)?.addEventListener('click', () => {
    if (!profileLoaded) { profileLoaded = true; loadProfile(); }
    activateHubView('profil');
  });
  (el.querySelector('#hub-settings-btn') as HTMLButtonElement | null)?.addEventListener('click', () => {
    if (!settingsBuilt) { settingsBuilt = true; buildSettingsTab(); }
    activateHubView('settings');
  });
  (el.querySelector('#hub-bug-open-btn') as HTMLButtonElement | null)?.addEventListener('click', () => {
    import('./bug-report').then(m => m.openBugReporter());
  });

  // Auto-select from URL hash (#hub/<view>)
  const rawHash = location.hash.replace(/^#/, '').split('?')[0];
  const hashTab = rawHash.startsWith('hub/') ? rawHash.slice(4) : rawHash;
  if (hashTab === 'profil') (el.querySelector('#hub-profil-btn') as HTMLElement | null)?.click();
  else if (hashTab === 'settings') (el.querySelector('#hub-settings-btn') as HTMLElement | null)?.click();
  else if (['changelog', 'roadmap', 'bugs'].includes(hashTab) || (hashTab === 'admin' && isUserAdmin)) {
    (el.querySelector(`.hub-tab[data-tab="${hashTab}"]`) as HTMLElement | null)?.click();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // SESSIONS TAB — ported from lobby.ts
  // ══════════════════════════════════════════════════════════════════════════

  const newBtn          = el.querySelector('#hub-new-btn')       as HTMLButtonElement;
  const limitEl         = el.querySelector('#hub-limit')         as HTMLElement;
  const limitHintEl     = el.querySelector('#hub-limit-hint')    as HTMLElement;
  const refreshBtn      = el.querySelector('#hub-refresh-btn')   as HTMLButtonElement;
  const createForm      = el.querySelector('#hub-create-form')   as HTMLElement;
  const createCorpInput = el.querySelector('#hub-create-corp')   as HTMLInputElement;
  const cityNameInput   = el.querySelector('#hub-city-name')     as HTMLInputElement;
  const seedInput       = el.querySelector('#hub-seed')          as HTMLInputElement;
  const maxPlayersInput = el.querySelector('#hub-max-players')    as HTMLInputElement;
  const maxPlayersVal   = el.querySelector('#hub-max-players-val') as HTMLElement;
  const modeSelect      = el.querySelector('#hub-mode')          as HTMLSelectElement;
  const deadlineSelect  = el.querySelector('#hub-deadline')      as HTMLSelectElement;
  const passwordInput   = el.querySelector('#hub-create-password') as HTMLInputElement;
  const createBtn       = el.querySelector('#hub-create-btn')    as HTMLButtonElement;
  const cancelBtn       = el.querySelector('#hub-cancel-btn')    as HTMLButtonElement;
  const loadingEl       = el.querySelector('#hub-loading')       as HTMLElement;
  const statusEl        = el.querySelector('#hub-status')        as HTMLElement;
  const errEl           = el.querySelector('#hub-err')           as HTMLElement;
  const secMine         = el.querySelector('#hub-sec-mine')      as HTMLElement;
  const secNew          = el.querySelector('#hub-sec-new')       as HTMLElement;
  const secRunning      = el.querySelector('#hub-sec-running')   as HTMLElement;
  const secPast         = el.querySelector('#hub-sec-past')      as HTMLElement;
  const cardsMine       = el.querySelector('#hub-cards-mine')    as HTMLElement;
  const cardsNew        = el.querySelector('#hub-cards-new')     as HTMLElement;
  const cardsRunning    = el.querySelector('#hub-cards-running') as HTMLElement;
  const cardsPast       = el.querySelector('#hub-cards-past')    as HTMLElement;
  const emptyAll        = el.querySelector('#hub-empty-all')     as HTMLElement;
  const langBtn         = el.querySelector('#hub-lang-btn')      as HTMLButtonElement;
  const bugBtn          = el.querySelector('#hub-bug-btn')       as HTMLButtonElement | null;

  bugBtn?.addEventListener('click', () => {
    import('./bug-report').then(m => m.openBugReporter());
  });

  // ── Bot slot management ──

  const botRowsEl   = el.querySelector('#hub-bot-rows')  as HTMLElement;
  const botAddBtn   = el.querySelector('#hub-bot-add')   as HTMLButtonElement;
  const botFillBtn  = el.querySelector('#hub-bot-fill')  as HTMLButtonElement;
  const botClearBtn = el.querySelector('#hub-bot-clear') as HTMLButtonElement;
  const botHintEl   = el.querySelector('#hub-bot-hint')  as HTMLElement;

  let botSlots: BotSlot[] = [];
  let botCorpsList: BotCorp[] = [];
  let botCorpsLoaded = false;

  function getMaxBots(): number {
    return Math.max(0, parseInt(maxPlayersInput.value, 10) - 1);
  }

  function randomPick<T>(arr: readonly T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  /** True when no bot slot already holds the (corp, strategy) tuple. Server
   *  v1.7.0 treats (corp, strategy) as the bot's persistent identity — two
   *  slots with the same pair would fight over the same player account and
   *  the second join would 409. Enforce uniqueness client-side. */
  function isPairFree(corpName: string, strategy: string): boolean {
    return !botSlots.some(b => b.corpName === corpName && b.strategy === strategy);
  }

  /** Enumerate all (corp, strategy) pairs not yet used by botSlots. */
  function freePairs(): Array<{ corp: { name: string; temperament?: string }; strategy: string }> {
    const corps = botCorpsList.length > 0
      ? botCorpsList
      : [{ name: `Bot ${botSlots.length + 1}`, temperament: 'balanced' }];
    const pairs: Array<{ corp: { name: string; temperament?: string }; strategy: string }> = [];
    for (const c of corps) {
      for (const s of BOT_STRATEGIES) {
        if (isPairFree(c.name, s)) pairs.push({ corp: c, strategy: s });
      }
    }
    return pairs;
  }

  /** Pick a random unused (corp, strategy) pair. Returns null if the catalog
   *  is exhausted (24 corps × 6 strategies = 144 max). */
  function makeRandomBot(): BotSlot | null {
    const pairs = freePairs();
    if (pairs.length === 0) return null;
    const pick = pairs[Math.floor(Math.random() * pairs.length)];
    return {
      corpName: pick.corp.name,
      temperament: pick.corp.temperament ?? randomPick(BOT_TEMPERAMENTS),
      difficulty: 'normal',
      strategy: pick.strategy,
    };
  }

  function buildSelect(options: readonly string[], selected: string, i18nPrefix: string, onChange: (v: string) => void): HTMLSelectElement {
    const sel = document.createElement('select');
    for (const opt of options) {
      const o = document.createElement('option');
      o.value = opt;
      o.textContent = t(`lobby.bot_${i18nPrefix}_${opt}`);
      if (opt === selected) o.selected = true;
      sel.appendChild(o);
    }
    sel.addEventListener('change', () => onChange(sel.value));
    return sel;
  }

  function renderBotRows(): void {
    botRowsEl.innerHTML = '';
    const maxBots = getMaxBots();

    for (let i = 0; i < botSlots.length; i++) {
      const slot = botSlots[i];
      const card = document.createElement('div');
      card.className = 'hub-bot-card';

      const row = document.createElement('div');
      row.className = 'hub-bot-row';

      const idx = document.createElement('span');
      idx.className = 'hub-bot-idx';
      idx.textContent = `${i + 1}`;
      row.appendChild(idx);

      // Corp name shown as plain label (randomly assigned from botCorpsList on add).
      const nameLabel = document.createElement('span');
      nameLabel.className = 'hub-bot-name';
      nameLabel.textContent = slot.corpName;
      row.appendChild(nameLabel);
      row.appendChild(buildSelect(BOT_TEMPERAMENTS, slot.temperament, 'temperament', v => { botSlots[i].temperament = v; }));
      row.appendChild(buildSelect(BOT_DIFFICULTIES, slot.difficulty, 'difficulty', v => { botSlots[i].difficulty = v; }));
      row.appendChild(buildSelect(BOT_STRATEGIES, slot.strategy, 'strategy', v => { botSlots[i].strategy = v; }));

      const rmBtn = document.createElement('button');
      rmBtn.className = 'hub-bot-remove btn sm red';
      rmBtn.textContent = '✕';
      rmBtn.title = t('lobby.bot_remove');
      rmBtn.addEventListener('click', () => {
        botSlots.splice(i, 1);
        renderBotRows();
      });
      row.appendChild(rmBtn);

      card.appendChild(row);

      // Narrative tagline (M2): shown only if the server advertised a `key`
      // for this corp AND i18n has a tagline string for it. Older servers
      // without `key` → older fixtures → no tagline rendered (graceful degrade).
      const corp = botCorpsList.find(c => c.name === slot.corpName);
      if (corp?.key) {
        const tagKey = `botcorp.${corp.key}.tagline`;
        const tagText = t(tagKey);
        if (tagText && tagText !== tagKey) {
          const tag = document.createElement('div');
          tag.className = 'hub-bot-tagline';
          tag.textContent = `"${tagText}"`;
          card.appendChild(tag);
        }
      }

      botRowsEl.appendChild(card);
    }

    // Update button states
    botAddBtn.disabled = botSlots.length >= maxBots;
    botFillBtn.disabled = botSlots.length >= maxBots;
    botClearBtn.style.display = botSlots.length > 0 ? '' : 'none';

    // Hint
    const remaining = maxBots - botSlots.length;
    botHintEl.textContent = botSlots.length > 0
      ? t('hub.bot_hint', { n: botSlots.length, max: maxBots, free: remaining })
      : '';
  }

  botAddBtn.addEventListener('click', () => {
    if (botSlots.length >= getMaxBots()) return;
    const bot = makeRandomBot();
    if (bot) botSlots.push(bot);
    renderBotRows();
  });

  botFillBtn.addEventListener('click', () => {
    const maxBots = getMaxBots();
    while (botSlots.length < maxBots) {
      const bot = makeRandomBot();
      if (!bot) break; // catalog exhausted — stop silently
      botSlots.push(bot);
    }
    renderBotRows();
  });

  botClearBtn.addEventListener('click', () => {
    botSlots = [];
    renderBotRows();
  });

  function updateCitySize(): void {
    const maxP = parseInt(maxPlayersInput.value, 10) || 8;
    const mode = modeSelect.value || 'standard';
    const eff = Math.max(maxP, 4);
    const sectors = eff * (SECTORS_PER_PLAYER[mode] ?? 7);
    const citySizeEl = el.querySelector('#hub-city-size') as HTMLElement;
    if (citySizeEl) citySizeEl.textContent = `${sectors} ${t('lobby.city_size_sectors')}`;
  }

  maxPlayersInput.addEventListener('input', () => {
    maxPlayersVal.textContent = maxPlayersInput.value;
    updateCitySize();
    const maxBots = getMaxBots();
    if (botSlots.length > maxBots) {
      botSlots.length = maxBots;
      renderBotRows();
    } else {
      botAddBtn.disabled = botSlots.length >= maxBots;
      botFillBtn.disabled = botSlots.length >= maxBots;
    }
  });

  modeSelect.addEventListener('change', updateCitySize);

  // Loading dots
  let dotCount = 0;
  const dotsEl = el.querySelector('#hub-dots') as HTMLElement;
  const dotsTimer = setInterval(() => {
    if (!dotsEl || !document.contains(dotsEl)) { clearInterval(dotsTimer); return; }
    dotsEl.textContent = '.'.repeat((dotCount++ % 3) + 1);
  }, 400);

  function setStatus(msg: string, isErr = false): void {
    statusEl.textContent = isErr ? '' : msg;
    errEl.textContent    = isErr ? msg : '';
  }

  // Language toggle
  langBtn.addEventListener('click', () => {
    const next = SUPPORTED_LANGS.find(l => l !== getLang()) ?? 'de';
    setLang(next);
    window.location.reload();
  });

  // Admin mode toggle
  const adminBtn = el.querySelector('#hub-admin-btn') as HTMLButtonElement | null;
  if (adminBtn) {
    adminBtn.addEventListener('click', () => {
      adminMode = !adminMode;
      localStorage.setItem('corpllm_admin_mode', adminMode ? '1' : '0');
      adminBtn.classList.toggle('active', adminMode);
      loadSessions();
    });
  }

  // Auto-refresh on tab focus
  function onWindowFocus(): void {
    if (!document.contains(el)) { window.removeEventListener('focus', onWindowFocus); return; }
    loadSessions();
  }
  window.addEventListener('focus', onWindowFocus);

  // Header avatar: load from profile when available
  const headerAvatar = el.querySelector('#hub-header-avatar') as HTMLImageElement;
  headerAvatar.addEventListener('click', () => {
    // Open the profil view (now a header-button view, not a tab)
    (el.querySelector('#hub-profil-btn') as HTMLElement | null)?.click();
  });

  // ── Load sessions ──

  let maxActiveSessions = 3;
  let atSessionCap = false;
  let initialLoad = true;
  let sessionsLoading = false;

  async function loadSessions(): Promise<void> {
    if (sessionsLoading) return; // in-flight guard — focus/refresh can't stack fetches
    sessionsLoading = true;
    if (initialLoad) loadingEl.style.display = 'block';
    setStatus('');

    try {
      const res = await fetch(`${serverBase}/api/sessions`, {
        headers: jwt ? { 'Authorization': `Bearer ${jwt}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { sessions: SessionRow[]; maxActiveSessions: number };
      maxActiveSessions = data.maxActiveSessions || 3;
      const sessions = Array.isArray(data.sessions) ? data.sessions
                     : Array.isArray(data) ? data as unknown as SessionRow[]
                     : [];
      loadingEl.style.display = 'none';
      initialLoad = false;
      renderSections(sessions);
    } catch (e: unknown) {
      loadingEl.style.display = 'none';
      initialLoad = false;
      emptyAll.textContent = t('lobby.no_sessions_err');
      emptyAll.style.display = 'block';
      setStatus(t('lobby.err_load', { msg: e instanceof Error ? e.message : String(e) }), true);
    } finally {
      sessionsLoading = false;
    }
  }

  // ── Render session sections ──

  function renderSections(sessions: SessionRow[]): void {
    for (const c of [cardsMine, cardsNew, cardsRunning, cardsPast]) c.innerHTML = '';
    for (const s of [secMine, secNew, secRunning, secPast]) s.style.display = 'none';
    emptyAll.style.display = 'none';

    const mine: SessionRow[] = [];
    const open: SessionRow[] = [];
    const running: SessionRow[] = [];
    const past: SessionRow[] = [];
    const mineIds = new Set<string>();

    for (const s of sessions) {
      if (s.isMember) { mine.push(s); mineIds.add(s.id); }
    }
    for (const s of sessions) {
      if (mineIds.has(s.id)) continue;
      if (s.status === 'finished') { past.push(s); continue; }
      if (s.status === 'running' || s.day > 0) { running.push(s); continue; }
      open.push(s);
    }

    mine.sort((a, b) => b.day - a.day);
    open.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    running.sort((a, b) => b.day - a.day);
    past.sort((a, b) => b.day - a.day);

    const activeCount = mine.filter(s => s.status !== 'finished').length;
    atSessionCap = activeCount >= maxActiveSessions;

    limitEl.textContent = t('lobby.session_limit', { cur: String(activeCount), max: String(maxActiveSessions) });
    limitEl.classList.toggle('at-cap', atSessionCap);
    if (atSessionCap) {
      limitHintEl.textContent = t('lobby.session_limit_reached');
      limitHintEl.style.display = 'inline';
    } else {
      limitHintEl.style.display = 'none';
    }
    newBtn.disabled = atSessionCap || !isBetaUser;
    if (!isBetaUser) {
      newBtn.title = t('lobby.beta_required');
      const banner = el.querySelector('#hub-beta-banner') as HTMLElement;
      if (banner) banner.style.display = 'block';
    }

    if (mine.length === 0 && open.length === 0 && running.length === 0 && past.length === 0) {
      emptyAll.textContent = t('lobby.empty_sessions');
      emptyAll.style.display = 'block';
      return;
    }

    if (mine.length > 0) {
      secMine.style.display = 'block';
      for (const s of mine) cardsMine.appendChild(buildCard(s, 'mine'));
    }
    if (open.length > 0) {
      secNew.style.display = 'block';
      for (const s of open) cardsNew.appendChild(buildCard(s, 'new'));
    }
    if (running.length > 0) {
      secRunning.style.display = 'block';
      for (const s of running) cardsRunning.appendChild(buildCard(s, s.day <= 1 ? 'new' : 'running'));
    }
    if (past.length > 0) {
      secPast.style.display = 'block';
      for (const s of past) cardsPast.appendChild(buildCard(s, 'past'));
    }
  }

  // ── Build a single session card ──

  type SectionKind = 'mine' | 'new' | 'running' | 'past';

  function buildCard(session: SessionRow, section: SectionKind): HTMLElement {
    const players = session.players ?? [];
    const isFull  = players.length >= session.maxPlayers;
    const myCorpLower = savedCorpName().trim().toLowerCase();

    const cardClass = section === 'mine' ? ' is-mine'
                    : section === 'running' ? ' is-running'
                    : section === 'past' ? ' is-finished'
                    : '';

    const card = document.createElement('div');
    card.className = `hub-card${cardClass}`;

    const colors = session.playerColors ?? [];
    const names = session.playerNames ?? [];
    const avatars = session.playerAvatars ?? [];
    const joinedDates = session.playerJoinedAt ?? [];
    const sessionCounts = session.playerSessionCounts ?? [];
    const botFlags = session.playerIsBots ?? [];
    const playerChips = players.map((p, i) => {
      const isMe = !!myCorpLower && p.toLowerCase() === myCorpLower;
      const isBot = botFlags[i] === true;
      const color = colors[i] || '#00f5d4';
      const displayName = names[i] || '';
      const avatar = avatars[i] || '';
      const joinedAt = joinedDates[i] || '';
      const sessCount = sessionCounts[i] ?? 0;
      const avatarHtml = avatar
        ? `<img class="hub-popover-portrait" src="${escHtml(avatar)}" alt="" />`
        : '';
      const joinedStr = joinedAt ? new Date(joinedAt).toLocaleDateString(
        getLang() === 'de' ? 'de-DE' : 'en-US',
        { year: 'numeric', month: 'long', day: 'numeric' }
      ) : '';
      const statsHtml = (sessCount || joinedStr) ? `<div class="hub-popover-stats">
          ${sessCount ? `<span class="hub-popover-stat-label">${t('hub.popover_sessions')}</span><span class="hub-popover-stat-value">${sessCount}</span>` : ''}
          ${joinedStr ? `<span class="hub-popover-stat-label">${t('hub.popover_member_since')}</span><span class="hub-popover-stat-value">${joinedStr}</span>` : ''}
        </div>` : '';
      const popover = `<div class="hub-player-popover">
        <div class="hub-popover-header">
          ${avatarHtml}
          <div>
            <div class="hub-popover-name">${escHtml(p)}</div>
            ${displayName ? `<div class="hub-popover-subname">${escHtml(displayName)}</div>` : ''}
          </div>
        </div>
        <div class="hub-popover-badge">
          <span class="hub-popover-color" style="background:${color}"></span>
          <span class="hub-popover-label">${isMe ? t('hub.popover_you') : isBot ? t('hub.popover_bot') : t('hub.popover_player')}</span>
        </div>
        ${statsHtml}
      </div>`;
      const chipClasses = ['hub-card-player-chip'];
      if (isMe) chipClasses.push('me');
      if (isBot) chipClasses.push('bot');
      return `<span class="${chipClasses.join(' ')}" tabindex="0" role="button" aria-label="${escHtml(p)}">${escHtml(p)}${popover}</span>`;
    }).join('');

    const mode = session.mode || 'standard';
    const modeBadgeClass = mode === 'quick' ? 'quick' : mode === 'epic' ? 'epic' : '';
    const modeBadgeText = mode === 'quick' ? t('lobby.mode_badge_quick')
                        : mode === 'epic'  ? t('lobby.mode_badge_epic')
                        : t('lobby.mode_badge_standard');
    const modeTip = mode === 'quick' ? t('lobby.mode_tip_quick')
                  : mode === 'epic'  ? t('lobby.mode_tip_epic')
                  : t('lobby.mode_tip_standard');

    const statusKey = session.status === 'finished' ? 'finished'
                    : session.status === 'running' ? 'running'
                    : isFull ? 'full' : 'lobby';
    const badgeText = statusKey === 'finished' ? t('lobby.badge_finished')
                    : statusKey === 'running'  ? t('lobby.badge_running')
                    : statusKey === 'full'     ? t('lobby.badge_full')
                    : t('lobby.badge_lobby');

    // Thumb
    const thumb = document.createElement('div');
    thumb.className = 'hub-card-thumb';
    thumb.appendChild(buildSeedCanvas(session.seed ?? 0));
    card.appendChild(thumb);

    // Info
    const infoEl = document.createElement('div');
    const winnerHtml = session.status === 'finished' && session.winner
      ? `<div class="hub-winner">${t('lobby.winner_label', { name: escHtml(session.winner) })}</div>`
      : '';
    infoEl.innerHTML = `
      <div class="hub-card-name">
        ${escHtml(session.name)}${session.hasPassword ? `<span style="opacity:0.5;font-size:0.8em" title="${t('lobby.password_protected')}">&#x1f512;</span>` : ''}
        <span class="hub-card-seed">seed:${session.seed ?? 0}</span>
        <span class="hub-mode ${modeBadgeClass}" title="${escHtml(modeTip)}">${modeBadgeText}</span>
      </div>
      <div class="hub-card-meta">
        <span class="hub-badge ${statusKey}">${badgeText}</span>
        ${dotBar(players.length, session.maxPlayers)}
        <span>${players.length}/${session.maxPlayers}</span>
        ${session.botCount ? `<span style="font-size:0.75rem;color:var(--text-lo)">${t('lobby.bot_count', { count: session.botCount })}</span>` : ''}
        <span>${t('lobby.day_label', { day: session.day })}</span>
      </div>
      ${winnerHtml}
      ${players.length > 0 ? `<div class="hub-card-players-list">${playerChips}</div>` : ''}`;
    card.appendChild(infoEl);

    // Action column
    const joinEl = document.createElement('div');
    joinEl.className = 'hub-card-right';
    joinEl.appendChild(buildCardAction(session, section, isFull));

    // Admin delete button
    if (adminMode) {
      const delBtn = document.createElement('button');
      delBtn.className = 'hub-delete btn sm red';
      delBtn.textContent = t('lobby.delete_btn');
      delBtn.addEventListener('click', async () => {
        const msg = t('lobby.delete_confirm', { name: session.name });
        if (!confirm(msg)) return;
        delBtn.disabled = true;
        setStatus('');
        try {
          const res = await fetch(`${serverBase}/api/sessions/${encodeURIComponent(session.id)}`, {
            method: 'DELETE',
            headers: jwt ? { 'Authorization': `Bearer ${jwt}` } : {},
          });
          if (!res.ok) {
            const text = await res.text();
            throw new Error(text || `HTTP ${res.status}`);
          }
          loadSessions();
        } catch (e: unknown) {
          delBtn.disabled = false;
          setStatus(t('lobby.err_delete', { msg: e instanceof Error ? e.message : String(e) }), true);
        }
      });
      joinEl.appendChild(delBtn);
    }

    card.appendChild(joinEl);
    return card;
  }

  function buildCardAction(session: SessionRow, section: SectionKind, isFull: boolean): HTMLElement {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;flex-direction:column;align-items:flex-end;gap:6px;';

    // "My Games" -> rejoin + leave
    if (section === 'mine') {
      // Use saved corp name, or fall back to the player's corp name from server data
      const corp = savedCorpName() || (session.players && session.players.length > 0 ? session.players[0] : '');
      if (session.status !== 'finished') {
        const btn = document.createElement('button');
        btn.className = 'hub-join btn amber';
        btn.textContent = session.status === 'lobby' ? t('lobby.enter_btn') : t('lobby.rejoin_btn');
        if (!isBetaUser) { btn.disabled = true; btn.title = t('lobby.beta_required'); }
        btn.addEventListener('click', async () => {
          btn.disabled = true;
          setStatus('');
          try {
            const res = await fetch(`${serverBase}/api/sessions/${encodeURIComponent(session.id)}/join`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(jwt ? { 'Authorization': `Bearer ${jwt}` } : {}),
              },
              body: JSON.stringify({ corpName: corp, accentColor: savedAccent() }),
            });
            if (!res.ok) {
              const text = await res.text();
              throw new Error(text || `HTTP ${res.status}`);
            }
            const data = await res.json() as { sessionId: string; corpName?: string };
            // Server returns the resolved corp name (may differ if localStorage was cleared)
            const resolvedCorp = data.corpName || corp;
            if (resolvedCorp) saveCorpName(resolvedCorp);
            clearInterval(dotsTimer);
            onJoin(session.id, resolvedCorp);
          } catch (e: unknown) {
            btn.disabled = false;
            setStatus(t('lobby.err_join', { msg: e instanceof Error ? e.message : String(e) }), true);
          }
        });
        wrap.appendChild(btn);

        // API MODE button
        if (onApiMode) {
          const apiBtn = document.createElement('button');
          apiBtn.className = 'hub-join hub-api-btn btn sm';
          apiBtn.textContent = t('hub.api_mode_btn');
          apiBtn.addEventListener('click', () => onApiMode(session.id));
          wrap.appendChild(apiBtn);
        }

        // Leave button
        const leaveBtn = document.createElement('button');
        leaveBtn.className = 'hub-leave btn sm red';
        leaveBtn.textContent = t('lobby.leave_btn');
        if (!isBetaUser) { leaveBtn.disabled = true; leaveBtn.title = t('lobby.beta_required'); }
        leaveBtn.addEventListener('click', async () => {
          leaveBtn.disabled = true;
          setStatus('');
          try {
            const res = await fetch(`${serverBase}/api/sessions/${encodeURIComponent(session.id)}/leave`, {
              method: 'DELETE',
              headers: jwt ? { 'Authorization': `Bearer ${jwt}` } : {},
            });
            if (!res.ok) {
              const text = await res.text();
              throw new Error(text || `HTTP ${res.status}`);
            }
            loadSessions();
          } catch (e: unknown) {
            leaveBtn.disabled = false;
            setStatus(t('lobby.err_leave', { msg: e instanceof Error ? e.message : String(e) }), true);
          }
        });
        wrap.appendChild(leaveBtn);
      }
      return wrap;
    }

    // Past / Running -> no action
    if (section === 'past' || section === 'running') return wrap;

    // "New Games" -> join button
    if (!isBetaUser) {
      const span = document.createElement('span');
      span.className = 'hub-unavail';
      span.textContent = t('lobby.beta_required');
      wrap.appendChild(span);
      return wrap;
    }
    if (isFull || atSessionCap) {
      const span = document.createElement('span');
      span.className = 'hub-unavail';
      span.textContent = isFull ? t('lobby.full_label') : t('lobby.session_limit_reached');
      wrap.appendChild(span);
      return wrap;
    }

    const corp = savedCorpName();
    let corpInput: HTMLInputElement | null = null;

    if (corp) {
      const asLabel = document.createElement('div');
      asLabel.className = 'hub-join-as';
      asLabel.textContent = corp;
      wrap.appendChild(asLabel);
    } else {
      corpInput = document.createElement('input');
      corpInput.className = 'hub-form-input hub-join-input';
      corpInput.placeholder = t('lobby.corp_placeholder');
      corpInput.maxLength = 40;
      wrap.appendChild(corpInput);
    }

    // Color picker — disable already-taken colors
    const takenColors = new Set(session.playerColors ?? []);
    const colorRow = document.createElement('div');
    colorRow.style.cssText = 'display:flex; gap:4px; margin:4px 0; flex-wrap:wrap;';
    let selectedJoinColor = savedAccent();
    if (takenColors.has(selectedJoinColor)) {
      const freeColor = ACCENT_PRESETS.find(p => !takenColors.has(p.color));
      if (freeColor) selectedJoinColor = freeColor.color;
    }
    for (const preset of ACCENT_PRESETS) {
      const dot = document.createElement('button');
      const taken = takenColors.has(preset.color);
      dot.style.cssText = `width:18px; height:18px; border-radius:50%; border:2px solid ${
        preset.color === selectedJoinColor ? '#fff' : 'transparent'
      }; background:${preset.color}; cursor:${taken ? 'not-allowed' : 'pointer'}; opacity:${taken ? '0.25' : '1'}; transition:border-color 0.15s;`;
      dot.title = taken ? `${preset.name} (belegt)` : preset.name;
      dot.disabled = taken;
      if (!taken) {
        dot.addEventListener('click', () => {
          selectedJoinColor = preset.color;
          colorRow.querySelectorAll('button').forEach(b => { (b as HTMLElement).style.borderColor = 'transparent'; });
          dot.style.borderColor = '#fff';
          saveAccent(preset.color);
          document.documentElement.style.setProperty('--cyan', preset.color);
          document.documentElement.style.setProperty('--cyan-dim', preset.color + 'aa');
        });
      }
      colorRow.appendChild(dot);
    }
    wrap.appendChild(colorRow);

    // Password input for password-protected sessions
    let joinPasswordInput: HTMLInputElement | null = null;
    if (session.hasPassword) {
      joinPasswordInput = document.createElement('input');
      joinPasswordInput.type = 'password';
      joinPasswordInput.className = 'hub-form-input';
      joinPasswordInput.placeholder = t('lobby.password_placeholder');
      joinPasswordInput.maxLength = 64;
      joinPasswordInput.style.cssText = 'width:100%;margin-bottom:6px';
      wrap.appendChild(joinPasswordInput);
    }

    const btn = document.createElement('button');
    btn.className = 'hub-join btn amber';
    btn.textContent = t('lobby.join_btn');

    async function doJoin(): Promise<void> {
      const corpName = corp || (corpInput?.value.trim() ?? '');
      if (!corpName) {
        corpInput?.focus();
        setStatus(t('lobby.err_corp_name'), true);
        return;
      }
      if (session.hasPassword && !joinPasswordInput?.value) {
        joinPasswordInput?.focus();
        setStatus(t('lobby.err_password_required'), true);
        return;
      }
      btn.disabled = true;
      setStatus('');
      try {
        const res = await fetch(`${serverBase}/api/sessions/${encodeURIComponent(session.id)}/join`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(jwt ? { 'Authorization': `Bearer ${jwt}` } : {}),
          },
          body: JSON.stringify({
            corpName,
            accentColor: selectedJoinColor,
            ...(joinPasswordInput?.value ? { password: joinPasswordInput.value } : {}),
          }),
        });
        if (!res.ok) {
          const text = await res.text();
          if (res.status === 403) {
            btn.disabled = false;
            joinPasswordInput?.focus();
            setStatus(t('lobby.err_wrong_password'), true);
            return;
          }
          throw new Error(text || `HTTP ${res.status}`);
        }
        saveCorpName(corpName);
        clearInterval(dotsTimer);
        onJoin(session.id, corpName);
      } catch (e: unknown) {
        btn.disabled = false;
        setStatus(t('lobby.err_join', { msg: e instanceof Error ? e.message : String(e) }), true);
      }
    }

    btn.addEventListener('click', doJoin);
    if (corpInput) corpInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') doJoin(); });
    if (joinPasswordInput) joinPasswordInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') doJoin(); });
    wrap.appendChild(btn);
    return wrap;
  }

  // ── Create session ──

  newBtn.addEventListener('click', () => {
    const isOpen = createForm.classList.toggle('open');
    if (isOpen) {
      createCorpInput.value = savedCorpName();
      // Always start with zero bots. FILL button adds a random roster on demand,
      // +BOT adds one at a time. Previous localStorage-restore pre-populated the
      // list and confused users who expected an empty form.
      botSlots = [];
      renderBotRows();
      // Pre-fill city name with random cyberpunk name
      if (!cityNameInput.value.trim()) {
        cityNameInput.value = randomCityName();
      }
      updateCitySize();
      // Fetch bot corps from server (once)
      if (!botCorpsLoaded) {
        getBotCorps().then(list => {
          botCorpsList = list;
          botCorpsLoaded = true;
          renderBotRows();
        }).catch(() => { /* degrade silently — manual names still work */ });
      }
      cityNameInput.focus();
    }
  });

  cancelBtn.addEventListener('click', () => {
    createForm.classList.remove('open');
    cityNameInput.value = '';
    seedInput.value = '0';
    maxPlayersInput.value = '8';
    maxPlayersVal.textContent = '8';
    modeSelect.value = 'standard';
    deadlineSelect.value = '22';
    botSlots = [];
    renderBotRows();
    updateCitySize();
  });

  createCorpInput.addEventListener('input', () => {
    saveCorpName(createCorpInput.value.trim());
  });

  async function doCreate(): Promise<void> {
    const name     = cityNameInput.value.trim();
    const corpName = createCorpInput.value.trim();
    if (!name)     { cityNameInput.focus(); return; }
    if (!corpName) { createCorpInput.focus(); setStatus(t('lobby.err_corp_name'), true); return; }

    createBtn.disabled = true;
    setStatus('');
    try {
      const res = await fetch(`${serverBase}/api/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { 'Authorization': `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({
          name,
          corpName,
          seed: parseInt(seedInput.value, 10) || 0,
          maxPlayers: parseInt(maxPlayersInput.value, 10) || 8,
          mode: modeSelect.value || 'standard',
          deadlineHour: parseInt(deadlineSelect.value, 10),
          accentColor: savedAccent(),
          ...(passwordInput.value ? { password: passwordInput.value } : {}),
          ...(botSlots.length > 0 ? { bots: botSlots.map(b => ({
            corpName: b.corpName,
            temperament: b.temperament,
            difficulty: b.difficulty,
            strategy: b.strategy,
          })) } : {}),
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const data = await res.json() as { sessionId: string };
      saveCorpName(corpName);
      clearInterval(dotsTimer);
      onJoin(data.sessionId, corpName);
    } catch (e: unknown) {
      createBtn.disabled = false;
      setStatus(t('lobby.err_create', { msg: e instanceof Error ? e.message : String(e) }), true);
    }
  }

  createBtn.addEventListener('click', doCreate);
  cityNameInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') doCreate(); });
  refreshBtn.addEventListener('click', () => loadSessions());

  loadSessions();
  void loadMotd();

  async function loadMotd(): Promise<void> {
    const slot = el.querySelector('#hub-motd-slot') as HTMLElement | null;
    if (!slot) return;
    try {
      const motd: Motd = await getMotd();
      if (!motd.text || !motd.text.trim()) { slot.innerHTML = ''; return; }
      if (motd.expiresAt) {
        const exp = new Date(motd.expiresAt).getTime();
        if (Number.isFinite(exp) && exp <= Date.now()) { slot.innerHTML = ''; return; }
      }
      const hash = `${motd.text}|${motd.severity ?? 'info'}|${motd.updatedAt ?? ''}`;
      if (localStorage.getItem('corpllm_motd_dismissed') === hash) { slot.innerHTML = ''; return; }
      const sev = (motd.severity === 'warn' || motd.severity === 'crit') ? motd.severity : 'info';
      const textSafe = escHtml(motd.text);
      slot.innerHTML = `
        <div class="motd-banner ${sev}">
          <div class="motd-text">${textSafe}</div>
          <button class="motd-close" id="hub-motd-close" title="${t('motd.dismiss')}">✕</button>
        </div>`;
      const close = slot.querySelector('#hub-motd-close') as HTMLButtonElement | null;
      close?.addEventListener('click', () => {
        localStorage.setItem('corpllm_motd_dismissed', hash);
        slot.innerHTML = '';
      });
    } catch {
      slot.innerHTML = '';
    }
  }

  // Fetch and display server version + meta widgets (roadmap, bugtracker, changelog)
  const versionEl = el.querySelector('#hub-version') as HTMLElement;
  versionEl.textContent = formatVersion();

  // Changelog Client/Server sub-tabs (ARIA tablist)
  const subtabsEl = el.querySelector('.hub-subtabs');
  subtabsEl?.setAttribute('role', 'tablist');
  subtabsEl?.setAttribute('aria-label', 'Changelog');
  el.querySelectorAll<HTMLElement>('.hub-subtab').forEach(st => {
    st.setAttribute('role', 'tab');
    st.setAttribute('aria-selected', st.classList.contains('active') ? 'true' : 'false');
    st.addEventListener('click', () => {
      const which = st.dataset.clog;
      el.querySelectorAll<HTMLElement>('.hub-subtab').forEach(x => { const on = x === st; x.classList.toggle('active', on); x.setAttribute('aria-selected', on ? 'true' : 'false'); });
      const cl = el.querySelector('#hub-changelog-client') as HTMLElement | null;
      const sv = el.querySelector('#hub-changelog-server') as HTMLElement | null;
      if (cl) cl.style.display = which === 'client' ? '' : 'none';
      if (sv) sv.style.display = which === 'server' ? '' : 'none';
    });
  });

  type ChangelogVersion = { version: string; date: string; entries: Array<{ type: string; text: string; issue?: number | null }> };

  // Render one changelog stream (client or server) into its own container.
  // Independent per-section so a server/meta hiccup can't blank the client log.
  function renderChangelogSection(container: HTMLElement, log: ChangelogVersion[], repo: string): void {
    const issueUrl = (num: number) => `https://github.com/CORP-LLM/${repo}/issues/${num}`;
    if (!Array.isArray(log) || log.length === 0) {
      container.innerHTML = `<div class="hub-meta-item" style="color:var(--text-dim)">${escHtml(t('hub.changelog_empty'))}</div>`;
      return;
    }
    container.innerHTML = log.map(v => `
      <div class="hub-meta-version-head">${escHtml(v.version)} <span style="color:var(--text-dim);font-weight:400">${escHtml(v.date)}</span></div>
      ${v.entries.map(e => `<div class="hub-meta-item">
        <span class="hub-meta-type ${escHtml(e.type)}">${escHtml(e.type)}</span>
        ${e.issue ? `<a href="${sanitizeUrl(issueUrl(e.issue))}" target="_blank" rel="noopener">#${escHtml(e.issue)}</a>` : ''}
        <span>${escHtml(e.text)}</span>
      </div>`).join('')}
    `).join('');
  }

  const statusClass = (s: string) => {
    const sl = s.toLowerCase();
    if (sl === 'done') return 'done';
    if (sl === 'in progress') return 'progress';
    if (sl === 'ready') return 'ready';
    return 'todo';
  };
  const isDone = (s: string) => s.toLowerCase() === 'done';

  // Meta-driven tabs (roadmap / bugtracker / server changelog) + client changelog.
  // The client changelog (static /CHANGELOG.json) renders INDEPENDENTLY so a
  // server/meta failure can't blank it. Each meta tab shows loading → data |
  // empty | error so a tab is never a silent blank box (#228 regression fix).
  const roadmapEl       = el.querySelector('#hub-roadmap-items')   as HTMLElement | null;
  const bugsEl          = el.querySelector('#hub-bugs-items')      as HTMLElement | null;
  const changelogClientEl = el.querySelector('#hub-changelog-client') as HTMLElement | null;
  const changelogServerEl = el.querySelector('#hub-changelog-server') as HTMLElement | null;
  const metaMsg = (msg: string) => `<div class="hub-meta-item" style="color:var(--text-dim)">${escHtml(msg)}</div>`;

  if (roadmapEl)        roadmapEl.innerHTML        = metaMsg(t('hub.loading_meta'));
  if (bugsEl)           bugsEl.innerHTML           = metaMsg(t('hub.loading_meta'));
  if (changelogServerEl) changelogServerEl.innerHTML = metaMsg(t('hub.loading_meta'));

  // Client changelog — static file, independent of the server.
  fetch('/CHANGELOG.json').then(r => r.ok ? r.json() : []).catch(() => [])
    .then((clientLog: ChangelogVersion[]) => {
      if (changelogClientEl) renderChangelogSection(changelogClientEl, clientLog, 'corpllm-client');
    });

  // Meta — version + roadmap + bugs + server changelog (auth required since v1.0.3).
  fetch(`${serverBase}/api/meta`, { headers: jwt ? { 'Authorization': `Bearer ${jwt}` } : {} })
    .then(r => { if (!r.ok) throw new Error(`meta ${r.status}`); return r.json() as Promise<any>; })
    .then((meta: any) => {
      versionEl.textContent = formatVersionFull(meta);
      versionEl.title = formatVersionTooltip(meta);

      if (roadmapEl) {
        const open = (meta.roadmap ?? []).filter((i: any) => !isDone(i.status));
        roadmapEl.innerHTML = open.length > 0 ? open.map((item: any) => {
          const link = item.url ? `<a href="${sanitizeUrl(item.url)}" target="_blank" rel="noopener">${escHtml(item.title)}</a>` : escHtml(item.title);
          const num = item.number ? `<span style="color:var(--text-lo)">#${escHtml(item.number)}</span>` : '';
          return `<div class="hub-meta-item"><span class="hub-meta-status ${statusClass(item.status)}">${escHtml(item.status || '?')}</span> ${num} ${link}</div>`;
        }).join('') : metaMsg(t('hub.roadmap_empty'));
      }

      if (bugsEl) {
        const bugs = meta.bugs ?? [];
        const sorted = [...bugs.filter((i: any) => !isDone(i.status)), ...bugs.filter((i: any) => isDone(i.status))];
        bugsEl.innerHTML = sorted.length > 0 ? sorted.map((item: any) => {
          const done = isDone(item.status);
          const titleStyle = done ? 'text-decoration:line-through;opacity:0.5' : '';
          const link = item.url ? `<a href="${sanitizeUrl(item.url)}" target="_blank" rel="noopener" style="${titleStyle}">${escHtml(item.title)}</a>` : `<span style="${titleStyle}">${escHtml(item.title)}</span>`;
          const num = item.number ? `<span style="color:var(--text-lo)">#${escHtml(item.number)}</span>` : '';
          const labels = (item.labels || []).filter((l: string) => l.startsWith('prio:')).map((l: string) => `<span class="hub-meta-label">${escHtml(l)}</span>`).join('');
          return `<div class="hub-meta-item"><span class="hub-meta-status ${statusClass(item.status)}">${escHtml(item.status || '?')}</span> ${num} ${link} ${labels}</div>`;
        }).join('') : metaMsg(t('hub.bugs_empty'));
      }

      if (changelogServerEl) renderChangelogSection(changelogServerEl, meta.serverChangelog ?? [], 'corpllm-server');
    })
    .catch(() => {
      const err = metaMsg(t('hub.meta_error'));
      if (roadmapEl)        roadmapEl.innerHTML        = err;
      if (bugsEl)           bugsEl.innerHTML           = err;
      if (changelogServerEl) changelogServerEl.innerHTML = err;
      fetch(`${serverBase}/version`).then(r => r.json()).then((d: { version?: string }) => {
        if (d.version) versionEl.textContent = formatVersion(d.version);
      }).catch(() => { /* ignore */ });
    });

  // ══════════════════════════════════════════════════════════════════════════
  // PROFIL TAB
  // ══════════════════════════════════════════════════════════════════════════

  function loadProfile(): void {
    const container = el.querySelector('#hub-profile-content') as HTMLElement;

    Promise.all([getProfile(), getProviders(), getPreferences().catch(() => null)])
      .then(([profile, providers, prefs]) => {
        renderProfile(container, profile, providers, prefs?.portraitData || '');
        // Set header avatar + popup (prefer portrait)
        const src = prefs?.portraitData || profile.avatarUrl;
        if (src) {
          headerAvatar.src = src;
          const avatarWrap = el.querySelector('#hub-avatar-wrap') as HTMLElement;
          if (avatarWrap) avatarWrap.style.display = 'inline-block';
          const popupImg = el.querySelector('#hub-avatar-popup-img') as HTMLImageElement;
          if (popupImg) popupImg.src = src;
        }
      })
      .catch(() => {
        container.innerHTML = `<div class="hub-empty empty">${t('hub.profile_error')}</div>`;
      });
  }

  function renderProfile(container: HTMLElement, profile: PlayerProfile, providers: ProvidersResponse, portraitData = ''): void {
    const memberSince = new Date(profile.createdAt).toLocaleDateString(getLang() === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
    const avatarSrc = portraitData || profile.avatarUrl || '';

    container.innerHTML = `
      <div class="hub-profile-header">
        <div class="hub-avatar-wrap">
          <img class="hub-avatar-large" src="${escHtml(avatarSrc)}" alt="avatar"
            onerror="this.style.display='none'" />
          <div class="hub-avatar-popup"><img src="${escHtml(avatarSrc)}" alt="portrait" onerror="this.parentElement.style.display='none'" /></div>
        </div>
        <div>
          <div class="hub-profile-name">${escHtml(profile.name)}</div>
          <div class="hub-profile-email">${escHtml(profile.email)}</div>
        </div>
      </div>

      <div class="hub-profile-section">
        <div class="hub-profile-section-title">${t('hub.profile_providers')}</div>
        <div class="hub-provider-row">
          <div class="hub-provider-icon">G</div>
          <div class="hub-provider-name">Google</div>
          ${providers.google
            ? `<span class="hub-provider-status hub-provider-linked">${t('hub.provider_linked')}</span>`
            : `<button class="hub-link-btn btn sm cyan" id="hub-link-google">${t('hub.provider_link')}</button>`}
        </div>
        <div class="hub-provider-row">
          <div class="hub-provider-icon">\u{1F4BB}</div>
          <div class="hub-provider-name">GitHub</div>
          ${providers.github
            ? `<span class="hub-provider-status hub-provider-linked">${t('hub.provider_linked')}</span>`
            : `<button class="hub-link-btn btn sm cyan" id="hub-link-github">${t('hub.provider_link')}</button>`}
        </div>
        <div class="hub-provider-row">
          <div class="hub-provider-icon">\u{1F3AE}</div>
          <div class="hub-provider-name">Discord</div>
          ${providers.discord
            ? `<span class="hub-provider-status hub-provider-linked">${t('hub.provider_linked')}</span>`
            : `<button class="hub-link-btn btn sm cyan" id="hub-link-discord">${t('hub.provider_link')}</button>`}
        </div>
      </div>

      <div class="hub-profile-section">
        <div class="hub-profile-section-title">${t('hub.profile_stats')}</div>
        <div class="hub-stat-row">
          <span class="hub-stat-label">${t('hub.stat_sessions')}</span>
          <span class="hub-stat-value">${profile.sessions?.length ?? 0}</span>
        </div>
        <div class="hub-stat-row">
          <span class="hub-stat-label">${t('hub.stat_member_since')}</span>
          <span class="hub-stat-value">${memberSince}</span>
        </div>
      </div>

      <div class="hub-profile-section">
        <button class="hub-logout btn red" id="hub-profile-logout">${t('settings.logout_btn')}</button>
      </div>
    `;

    // Link buttons
    const linkGoogle = container.querySelector('#hub-link-google') as HTMLButtonElement | null;
    const linkGitHub = container.querySelector('#hub-link-github') as HTMLButtonElement | null;
    const linkDiscord = container.querySelector('#hub-link-discord') as HTMLButtonElement | null;

    if (linkGoogle) {
      linkGoogle.addEventListener('click', () => {
        sessionStorage.setItem('corpllm_link_mode', '1');
        startGoogleLogin();
      });
    }
    if (linkGitHub) {
      linkGitHub.addEventListener('click', () => {
        sessionStorage.setItem('corpllm_link_mode', '1');
        startGitHubLogin();
      });
    }
    if (linkDiscord) {
      linkDiscord.addEventListener('click', () => {
        sessionStorage.setItem('corpllm_link_mode', '1');
        startDiscordLogin();
      });
    }

    // Logout
    container.querySelector('#hub-profile-logout')!.addEventListener('click', () => {
      clearJwt();
      location.reload();
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // SETTINGS TAB — ported from settings.ts
  // ══════════════════════════════════════════════════════════════════════════

  function buildSettingsTab(): void {
    const container = el.querySelector('#hub-settings-content') as HTMLElement;
    container.innerHTML = '';
    const settingsEl = renderSettings();
    container.appendChild(settingsEl);
  }

  function buildAdminTab(): void {
    const container = el.querySelector('#hub-admin-content') as HTMLElement;
    if (!container) return;
    container.innerHTML = '';
    const adminEl = renderAdmin();
    container.appendChild(adminEl);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Load header avatar on init
  // ══════════════════════════════════════════════════════════════════════════

  if (jwt) {
    // Load avatar: prefer character portrait from preferences, fall back to OAuth avatar
    Promise.all([
      getProfile().catch(() => null),
      getPreferences().catch(() => null),
    ]).then(([profile, prefs]) => {
      const portraitSrc = prefs?.portraitData || '';
      const oauthSrc = profile?.avatarUrl || '';
      const src = portraitSrc || oauthSrc;
      if (src) {
        headerAvatar.src = src;
        const avatarWrap = el.querySelector('#hub-avatar-wrap') as HTMLElement;
        if (avatarWrap) avatarWrap.style.display = 'inline-block';
        const popupImg = el.querySelector('#hub-avatar-popup-img') as HTMLImageElement;
        if (popupImg) popupImg.src = src;
      }
    });
  }

  return el;
}
